import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal from '../General/Modal';
import cn from 'classnames';
import { ReviewRating } from '../Reviews/ReviewRating';
import { getThingtestingMediaImageUrl, getThingtestingImageUrl } from '../../Helpers/thingtesting_helpers';
import './ReviewModal.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faTimes, faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { getPrettyTimeAgoFromNow } from '../../Helpers/formatting';
import { copyToClipboard } from '../../Helpers/helpers';
import cogoToast from 'cogo-toast';

const getAffiliationText = (affiliation, isIncentivized, brandTitle) => {
  if (isIncentivized) {
    return 'Incentivized review';
  }

  if (!affiliation) {
    return null;
  }

  switch (affiliation) {
    case 'employee':
      return `Works for ${brandTitle}`;
    case 'friend':
      return `Friends with ${brandTitle}`;
    case 'influencer':
      return `Influencer for ${brandTitle}`;
    case 'investor':
      return `Invested in ${brandTitle}`;
    case 'free product':
      return `Received a free product from ${brandTitle}`;
    case 'thingdrop discount':
    case 'sampling discount':
      return 'Verified purchase';
    default:
      return affiliation.charAt(0).toUpperCase() + affiliation.slice(1); // Capitalize first letter
  }
};

const ReviewModal = ({ review, otherReviews, visible, close, onNavigate }) => {
  const thumbRefs = useRef([]);
  const contentRef = useRef(null);

  const { brand, author = {}, stars, bodyPositive, bodyNegative, media = [], title, publishedAt, recommended, affiliation, post } = review;

  const reviewText = [bodyPositive, bodyNegative].filter(Boolean).join(' ');
  const reviewUrl = brand && post?.id ? `https://thingtesting.com/brands/${brand.slug}/posts/${post.id}?utm_source=shopmy` : '#';

  const isIncentivized = post && post.isIncentivized;
  const affiliationText = getAffiliationText(affiliation, isIncentivized, brand.title);

  const navigate = (e, delta) => {
    e.preventDefault();
    const currentIndex = otherReviews.findIndex(r => r.id === review.id);
    const newIndex = currentIndex + delta;
    if (newIndex >= 0 && newIndex < otherReviews.length) {
      onNavigate(otherReviews[newIndex]);
    } else if (newIndex < 0) {
      onNavigate(otherReviews[otherReviews.length - 1]);
    } else if (newIndex >= otherReviews.length) {
      onNavigate(otherReviews[0]);
    }
  };

  const share = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ url: reviewUrl });
      } catch (error) {
        console.error('Error sharing review', error);
      }
    } else {
      copyToClipboard(reviewUrl);
      cogoToast.success('Review URL copied to clipboard');
    }
  };

  const activeIndex = otherReviews.findIndex(r => r.id === review.id);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });

    thumbRefs.current[activeIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }, [activeIndex]);

  return (
    <Modal
      showClose
      noPadding
      visible={visible}
      close={close}
      className='review-modal-outer-container'
      contentClassName='review-modal-content'
      isNestedModal
    >
      <div className='review-modal-container'>
        <div className='review-modal-nav-left' role='button' aria-label='Previous' onClick={e => navigate(e, -1)}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </div>
        <div className='review-modal-nav-right' role='button' aria-label='Next' onClick={e => navigate(e, 1)}>
          <FontAwesomeIcon icon={faChevronRight} />
        </div>

        <div className='review-modal-content-container' ref={contentRef}>
          <div className='review-modal-header'>
            <div className='review-modal-close' role='button' aria-label='Close' onClick={close}>
              Close
              <FontAwesomeIcon onClick={close} icon={faTimes} />
            </div>
          </div>
          {media.length > 0 && (
            <div className='review-modal-image'>
              <img src={getThingtestingMediaImageUrl(media[0])} alt={title || 'Review image'} />

              <div className='review-modal-nav-left mobile' role='button' aria-label='Previous' onClick={e => navigate(e, -1)}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </div>
              <div className='review-modal-nav-right mobile' role='button' aria-label='Next' onClick={e => navigate(e, 1)}>
                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </div>
          )}

          <div className='review-modal-details'>
            <div className='review-author-row'>
              {author.avatarFullPath && (
                <img className='review-author-avatar' src={getThingtestingImageUrl(author.avatarFullPath, 'w-128,h-128')} alt={author.fullName} />
              )}
              <div className='review-author-meta'>
                <div className='review-author-name'>{author.fullName}</div>
                {publishedAt && <div className='review-date'>{getPrettyTimeAgoFromNow(publishedAt, { longForm: true })}</div>}
              </div>
            </div>

            <div className='review-content'>
              {typeof stars === 'number' && <ReviewRating rating={stars} />}

              {recommended && <div className='review-recommend-label'>Would recommend</div>}

              {brand && <h3 className='review-title'>{brand.title}</h3>}

              {reviewText && <p className='review-body'>{reviewText}</p>}
              {affiliationText && (
                <p className='affiliation-text'>
                  <FontAwesomeIcon className='affiliation-info-icn' icon={faInfoCircle} />
                  {affiliationText}
                </p>
              )}
            </div>

            <div className='review-actions'>
              <button type='button' className='review-share-btn review-label-button' onClick={share}>
                Share
              </button>
              <a className='review-view-btn review-label-button' href={reviewUrl} target='_blank' rel='noopener noreferrer'>
                View on Thingtesting
                <FontAwesomeIcon className='external' icon={faExternalLink} />
              </a>
            </div>
          </div>
        </div>

        <div className='review-modal-thumbs'>
          {otherReviews.map((review, idx) => (
            <div
              className={cn('review-modal-thumb', { active: idx === activeIndex })}
              key={idx}
              role='link'
              onClick={() => onNavigate(review)}
              ref={el => (thumbRefs.current[idx] = el)}
            >
              {review.media.length > 0 ? (
                <img src={getThingtestingMediaImageUrl(review.media[0], 'w-128,h-128')} alt={review.title || ''} />
              ) : (
                <div className='review-modal-thumb-placeholder' />
              )}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

ReviewModal.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.string,
    brand: PropTypes.shape({ slug: PropTypes.string }),
    author: PropTypes.shape({
      fullName: PropTypes.string,
      avatarFullPath: PropTypes.string
    }),
    stars: PropTypes.number,
    bodyPositive: PropTypes.string,
    bodyNegative: PropTypes.string,
    media: PropTypes.arrayOf(
      PropTypes.shape({
        filePath: PropTypes.string
      })
    ),
    title: PropTypes.string,
    publishedAt: PropTypes.string,
    recommended: PropTypes.bool
  }),
  visible: PropTypes.bool,
  close: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired
};

ReviewModal.defaultProps = {
  visible: false,
  close: () => {}
};

export default ReviewModal;
